import { civilParts } from '../../lib/timeZone';

/**
 * "Good afternoon, Kunal" — told by the TENANT's clock, not the reader's.
 *
 * Everything on this screen is: the window's day boundaries, the chart buckets,
 * every date printed. The greeting is the one line that read `new Date()`
 * directly, so a dispatcher in the office at 9am was wished a good evening by a
 * laptop still set to the timezone it was bought in — the same bug, in the most
 * visible sentence on the page. `civilParts` is the one place an instant
 * becomes wall-clock numbers, so the greeting asks it too.
 *
 * The boundaries are dispatch-dashboard's own (`DashboardHeader._greeting`):
 * before noon, before 17:00, after. The two apps greet the same person on the
 * same screen, so a boundary moved on one side is a person told "morning" by
 * the classic dashboard and "afternoon" by this one at the same minute.
 */

export type PartOfDay = 'morning' | 'afternoon' | 'evening';

export const partOfDay = (now: Date, zone: string | null | undefined): PartOfDay => {
  const { hour } = civilParts(now, zone);
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};

/**
 * The line itself. A name that is missing or blank drops the comma rather than
 * greeting nobody ("Good morning, ") — the host may not send one, and `/meta`
 * answers `name: null` for a dispatch profile with no name on it.
 */
export const greetingFor = (
  name: string | null | undefined,
  now: Date,
  zone: string | null | undefined,
): string => {
  const salute = `Good ${partOfDay(now, zone)}`;
  const trimmed = name?.trim();
  return trimmed ? `${salute}, ${trimmed}` : salute;
};
